import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useOrder } from '../context/OrderContext';
import {
    ChevronRight,
    ChefHat,
    Clock,
    UtensilsCrossed,
    X
} from 'lucide-react';

export const TrackOrderButton = () => {
    const { activeOrder } = useOrder();
    const location = useLocation();
    const [isVisible, setIsVisible] = useState(true);

    // Reset visibility when a new active order comes in or changes
    useEffect(() => {
        if (activeOrder) setIsVisible(true);
    }, [activeOrder?.id]);

    // Hide if no active order OR if already on tracking page OR dismissed
    if (!activeOrder || location.pathname.includes('/track') || !isVisible) return null;

    const rawStatusUpper = (activeOrder.status || 'PLACED').toUpperCase();

    let orderStatus = 'Placed';
    if (rawStatusUpper.includes('PREPAR')) {
        orderStatus = 'Preparing';
    } else if (rawStatusUpper.includes('READY') || rawStatusUpper.includes('SERV') || rawStatusUpper.includes('DELIVER') || rawStatusUpper.includes('COMPLET')) {
        orderStatus = 'Ready';
    }

    // Progress calculation for a radius of 26
    // Circumference = 2 * PI * 26 = ~163.36
    const radius = 26;
    const circumference = 2 * Math.PI * radius;
    const progressPercent = orderStatus === 'Placed' ? 25 : orderStatus === 'Preparing' ? 65 : 100;
    const offset = circumference - (progressPercent / 100) * circumference;

    const handleClick = () => {
        if (window.REACT_APP_NAVIGATE) {
            window.REACT_APP_NAVIGATE(`/track/${activeOrder.orderNumber || activeOrder.id}`);
        }
    };

    return (
        <div className="fixed bottom-10 left-10 z-[100] animate-in fade-in slide-in-from-bottom-6 duration-700 ease-out">
            {/* Premium Glow Effect */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-[#d4af37] to-[#8b6d1b] rounded-[22px] blur-md opacity-20 transition duration-1000 group-hover:opacity-40"></div>

            <div
                onClick={handleClick}
                className="relative flex items-center bg-[#140e0c]/95 backdrop-blur-md border border-[#d4af37]/30 rounded-[20px] p-2 pr-4 shadow-2xl min-w-[260px] group cursor-pointer hover:border-[#d4af37]/60 transition-all duration-300"
            >
                {/* Progress Circle Container */}
                <div className="relative flex-shrink-0 w-14 h-14 flex items-center justify-center">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 64 64">
                        {/* Background Track */}
                        <circle
                            cx="32"
                            cy="32"
                            r={radius}
                            stroke="currentColor"
                            strokeWidth="3.5"
                            fill="transparent"
                            className="text-white/5"
                        />
                        {/* Active Progress */}
                        <circle
                            cx="32"
                            cy="32"
                            r={radius}
                            stroke="#d4af37"
                            strokeWidth="3.5"
                            strokeLinecap="round"
                            fill="transparent"
                            strokeDasharray={circumference}
                            style={{
                                strokeDashoffset: offset,
                                transition: 'stroke-dashoffset 1.5s ease-in-out'
                            }}
                        />
                    </svg>

                    {/* Center Icon based on status */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        {orderStatus === 'Preparing' ? (
                            <div className="relative flex items-center justify-center">
                                <ChefHat className="text-[#d4af37] w-5 h-5 animate-pulse" />
                                <span className="absolute -top-1 -right-1 flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#d4af37] opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#d4af37]"></span>
                                </span>
                            </div>
                        ) : orderStatus === 'Ready' ? (
                            <UtensilsCrossed className="text-[#d4af37] w-5 h-5" />
                        ) : (
                            <Clock className="text-[#d4af37] w-5 h-5" />
                        )}
                    </div>
                </div>

                {/* Order Details */}
                <div className="ml-3 flex-grow py-1">
                    <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[9px] font-black uppercase tracking-[0.25em] text-[#d4af37]">Active Order</span>
                        <button
                            onClick={(e) => { e.stopPropagation(); setIsVisible(false); }}
                            className="p-1 -mr-1 text-white/20 hover:text-[#d4af37] transition-colors rounded-full"
                            title="Dismiss"
                        >
                            <X size={14} />
                        </button>
                    </div>
                    <h3 className="text-white font-medium text-[13px] tracking-tight">
                        {orderStatus === 'Preparing' ? 'Kitchen is preparing...' : orderStatus === 'Ready' ? 'Order is ready!' : 'Order received...'}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-0.5">
                        <div className="w-1 h-1 rounded-full bg-[#d4af37] animate-pulse"></div>
                        <span className="text-[10px] text-gray-400 font-medium tracking-wide capitalize">
                            {rawStatusUpper.replace(/_/g, ' ')}
                        </span>
                    </div>
                </div>

                {/* Navigation Arrow */}
                <div className="ml-3 h-8 w-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-[#d4af37] group-hover:border-[#d4af37] group-hover:scale-110 transition-all duration-300 shadow-inner">
                    <ChevronRight className="text-white group-hover:text-black w-4 h-4" />
                </div>
            </div>

            {/* Responsive adjustments */}
            <style>{`
                @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slide-in-bottom { from { transform: translateY(20px); } to { transform: translateY(0); } }
                
                .animate-in { 
                    animation: fade-in 0.7s ease-out forwards, slide-in-bottom 0.7s ease-out forwards; 
                }
                
                @media (max-width: 640px) {
                    .fixed {
                        left: 1rem !important;
                        right: 1rem !important;
                        bottom: 1.5rem !important;
                    }
                    .min-w-\\[260px\\] {
                        width: 100% !important;
                        min-width: 100% !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default React.memo(TrackOrderButton);
